// src/services/invoiceService.js
/**
 * Invoice Service - Handles all invoice-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 * 
 * NOTE: Uses axios (not fetch) for automatic token refresh via AuthProvider interceptors
 */

import axios from 'axios';
import { generateExternalReference } from '../utils/invoiceUtils';
import { formatDateLocal } from '../utils/formatters';
import { INVOICE_FEATURES } from '../config';

/*
// // Import the NODE backend URL from config FOR CIA SERVER (direct IP, no nginx proxy, for testing on LAN)
const getAPIBaseURL = () => {
    // Dynamically import to avoid circular dependencies
    try {
        // Use the NODE_BACKEND_API_URL which includes /apitest
        const configURL = "http://192.168.0.4:8096/apitest"; // Your VPS base URL with /apitest
        return configURL;
    } catch (error) {
        console.warn('Could not load config, falling back to localhost:', error);
        return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8096/apitest';
    }
};
*/



// Import the NODE backend URL from config 
const getAPIBaseURL = () => {
    // Dynamically import to avoid circular dependencies
    try {
        // Use the NODE_BACKEND_API_URL which includes /apitest
        const configURL = "https://horizontepyme.proyectoshorizontales.com/apitest"; // Your VPS base URL with /apitest
        return configURL;
    } catch (error) {
        console.warn('Could not load config, falling back to localhost:', error);
        return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8096/apitest';
    }
};



const INVOICE_API_URL = `${getAPIBaseURL()}/invoices`;

/**
 * Get clientDatabaseName from localStorage authUser
 * @returns {string} Client database name or 'DUTTO_TEST' as fallback
 */
const getClientDatabaseName = () => {
    try {
        const authUserString = localStorage.getItem('authUser');
        if (authUserString) {
            const authUser = JSON.parse(authUserString);
            if (authUser?.clientDatabaseName) {
                console.log('📍 [InvoiceService] Using clientDatabaseName from authUser:', authUser.clientDatabaseName);
                return authUser.clientDatabaseName;
            }
        }
        console.warn('⚠️ [InvoiceService] No clientDatabaseName in localStorage, using fallback: DUTTO_TEST');
    } catch (error) {
        console.error('❌ [InvoiceService] Error reading clientDatabaseName from localStorage:', error);
    }
    return 'DUTTO_TEST'; // Fallback
};

/**
 * Map condicion_impositiva (full name) to condicion_iva (AFIP code)
 * @param {string} condicionImpositiva - Full tax condition name
 * @returns {string} AFIP code (RI, CF, M, EX)
 */
const mapTaxConditionToAFIPCode = (condicionImpositiva) => {
    const mapping = {
        'RESPONSABLE INSCRIPTO': 'RI',
        'CONSUMIDOR FINAL': 'CF',
        'MONOTRIBUTO': 'M',
        'EXENTO': 'EX'
    };
    
    const normalized = condicionImpositiva?.toUpperCase().trim();
    return mapping[normalized] || 'CF'; // Default to CF if unknown
};

/**
 * Calculate IVA details from retail price (price with IVA included)
 * @param {number} retailPrice - Price with IVA included
 * @param {number} ivaRate - IVA rate percentage (default 21%)
 * @returns {Object} Object with basePrice, ivaAmount, totalPrice, alicuota
 */
const calculateIVAFromRetailPrice = (retailPrice, ivaRate = 21) => {
    const divisor = 1 + (ivaRate / 100);
    const basePrice = retailPrice / divisor;
    const ivaAmount = retailPrice - basePrice;
    
    return {
        basePrice: Number(basePrice.toFixed(2)),      // Price without IVA
        ivaAmount: Number(ivaAmount.toFixed(2)),      // IVA amount
        totalPrice: Number(retailPrice.toFixed(2)),   // Price with IVA (original)
        alicuota: ivaRate                              // IVA rate (21, 10.5, 27, etc.)
    };
};

/**
 * Determine if an invoice item should affect stock
 * @param {string} invoiceType - Invoice type (A, B, C, X, or credit note types)
 * @param {number|null} productId - Product ID (null for custom products)
 * @param {boolean} isBudgetQuote - Whether this is a BUDGET_QUOTE (quote/presupuesto that's not a sale)
 * @returns {boolean} true if stock should be affected
 */
const shouldAffectStock = (invoiceType, productId, isBudgetQuote = false) => {
    // Custom products (no product ID) don't affect stock
    if (!productId || productId <= 0) {
        return false;
    }
    
    // BUDGET_QUOTE documents (Presupuesto - No Registra Venta) don't affect stock
    // These are quotes/budgets that are NOT sales
    // NOTE: Factura X (invoiceType === 'X') IS a sale and DOES affect stock,
    // even if not registered with AFIP. Only BUDGET_QUOTE documents skip stock.
    if (isBudgetQuote === true) {
        return false;
    }
    
    // Credit notes RESTORE stock (positive change)
    if (invoiceType && invoiceType.toString().toUpperCase().includes('NOTA DE CREDITO')) {
        return true;  // Credit notes DO affect stock (add back)
    }
    
    // All invoice types (A, B, C, X) affect stock - they are all sales
    return true;
};

/**
 * Transform cart items for invoice generation based on invoice type
 * For FACTURA A: Calculate base price and IVA separately
 * For FACTURA B/C: Use retail price as-is
 * @param {Array} cartItems - Original cart items
 * @param {string} invoiceType - Invoice type (A, B, C, X)
 * @param {boolean} isBudgetQuote - Whether this is a BUDGET_QUOTE (not a sale)
 * @returns {Array} Transformed cart items with IVA details
 */
const transformCartItemsWithIVA = (cartItems, invoiceType, isBudgetQuote = false) => {
    return cartItems.map(item => {
        // Calculate the true unit price (RetailPrice should already be unit price, but ensure it)
        // If RetailPrice seems to be line total, divide by quantity
        const unitPrice = item.RetailPrice || 0;
        const quantity = item.quantity || 1;
        const splitter = item.splitter || 1;
        
        // Unit price after considering splitter
        const effectiveUnitPrice = unitPrice / splitter;
        
        const baseItem = {
            ProductId: item.ProductId,
            ProductDescription: item.ProductDescription,
            ProductCode: item.ProductCode,
            EAN: item.EAN,
            BrandName: item.BrandName,
            CategoryName: item.CategoryName,
            quantity: quantity,
            splitter: splitter,
            StockQuantity: item.StockQuantity,
            afecta_stock: shouldAffectStock(invoiceType, item.ProductId, isBudgetQuote) // Determine if stock should be affected
        };

        // For FACTURA A: Calculate IVA separately (IVA discriminado)
        if (invoiceType === 'A') {
            const ivaDetails = calculateIVAFromRetailPrice(effectiveUnitPrice, 21);
            return {
                ...baseItem,
                RetailPrice: effectiveUnitPrice,          // Unit price (with IVA)
                BasePrice: ivaDetails.basePrice,          // Unit price without IVA
                IVAAmount: ivaDetails.ivaAmount,          // IVA amount per unit
                IVARate: ivaDetails.alicuota,             // IVA rate (21)
                // Backend compatibility fields
                precio_con_iva: ivaDetails.totalPrice,
                precio_sin_iva: ivaDetails.basePrice,
                alicuota: ivaDetails.alicuota
            };
        }
        
        // For FACTURA B/C/X: Price includes IVA, no discrimination needed
        // But still need to send UNIT price, not line total
        const ivaDet = calculateIVAFromRetailPrice(effectiveUnitPrice, 21);
        return {
            ...baseItem,
            RetailPrice: effectiveUnitPrice,              // Unit price (with IVA)
            BasePrice: ivaDet.basePrice,                  // Unit price without IVA (for B/C calculation)
            IVAAmount: ivaDet.ivaAmount,                  // IVA per unit
            IVARate: 21,                                   // Always 21% for B/C
            precio_con_iva: effectiveUnitPrice,
            precio_sin_iva: ivaDet.basePrice,             // Send unit price WITHOUT IVA
            alicuota: 21                                   // 21% IVA for B/C
        };
    });
};

// NOTE: getAuthToken and getAuthHeaders have been REMOVED
// Token handling is now automatic via axios interceptors in AuthProvider.js
// This ensures automatic token refresh when access token expires (after ~30 min)

/**
 * Handle API response and check for errors
 * @param {Object} data - Axios response data (already parsed JSON)
 * @returns {Object} Validated response data
 * @throws {Error} If response contains error
 */
const handleApiResponse = (data) => {
    // Check if backend returned an error in success response
    if (data.success === false) {
        throw new Error(data.message || data.error || 'Unknown error occurred');
    }
    
    return data;
};

/**
 * Check if cart total is within AFIP amount limits
 * @param {number} total - Cart total amount
 * @returns {Promise<Object>} { success: boolean, data: { withinLimit: boolean, limit: number, currentAmount: number } }
 */
export const checkAmountLimit = async (total) => {
    try {
        const response = await axios.post(`${INVOICE_API_URL}/limit-check`, { total });
        return handleApiResponse(response.data);
    } catch (error) {
        console.error('Error checking amount limit:', error);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(`Amount validation failed: ${errorMessage}`);
    }
};

/**
 * Create invoice from cart data
 * @param {Object} cartData - Cart information
 * @param {Array} cartData.cartItems - Array of cart items
 * @param {number} cartData.subtotal - Subtotal amount
 * @param {number} cartData.total - Total amount after discount
 * @param {number} cartData.discountPercentage - Discount percentage
 * @param {number} cartData.discountAmount - Discount amount
 * @param {string} cartData.sessionId - Optional cart session ID
 * @returns {Promise<Object>} Invoice creation response with CAE, reference, etc.
 */
export const createInvoice = async (cartData) => {
    try {
        // Validate required cart data
        if (!cartData.cartItems || cartData.cartItems.length === 0) {
            throw new Error('Cart is empty - cannot generate invoice');
        }
        
        if (!cartData.total || cartData.total <= 0) {
            throw new Error('Invalid cart total - cannot generate invoice');
        }
        
        // Transform clientData to include condicion_iva (AFIP code) if condicion_impositiva exists
        let transformedClientData = cartData.clientData || null;
        if (transformedClientData && transformedClientData.condicion_impositiva) {
            transformedClientData = {
                ...transformedClientData,
                condicion_iva: mapTaxConditionToAFIPCode(transformedClientData.condicion_impositiva)
            };
            console.log('[Invoice Service] Mapped tax condition:', transformedClientData.condicion_impositiva, '→', transformedClientData.condicion_iva);
        }
        
        // Transform cart items with IVA calculation for FACTURA A
        // isBudgetQuote comes from the documentType selection in the modal
        // Factura X is NOT a budget quote - it's a sale (even if not registered with AFIP)
        const isBudgetQuote = cartData.isBudgetQuote === true;
        const transformedCartItems = transformCartItemsWithIVA(
            cartData.cartItems, 
            cartData.invoiceType || 'C',
            isBudgetQuote
        );
        
        console.log('[Invoice Service] Transformed cart items for invoice type', cartData.invoiceType);
        console.log('[Invoice Service] isBudgetQuote:', isBudgetQuote);
        console.log('[Invoice Service] Sample item transformation:', {
            original: cartData.cartItems[0],
            transformed: transformedCartItems[0]
        });
        console.log('[Invoice Service] afecta_stock values:', transformedCartItems.map(item => ({
            ProductCode: item.ProductCode,
            ProductId: item.ProductId,
            afecta_stock: item.afecta_stock
        })));
        
        const payload = {
            cartItems: transformedCartItems,  // Use transformed items with IVA calculation (includes afecta_stock)
            subtotal: cartData.subtotal,
            total: cartData.total,
            discountPercentage: cartData.discountPercentage || 0,
            discountAmount: cartData.discountAmount || 0,
            sessionId: cartData.sessionId || `cart_${Date.now()}`,
            externalReference: generateExternalReference(), // Add frontend-generated external reference
            invoiceType: cartData.invoiceType || 'C',        // Invoice type (A, B, C, X)
            paymentMethod: cartData.paymentMethod || '201',  // Payment method code (201-208)
            clientData: transformedClientData,               // NEW: Client data with condicion_iva
            // Use registerWithAFIP from cartData (set by modal), with fallback to PRESUPUESTO config
            registerWithAFIP: cartData.registerWithAFIP !== undefined 
                ? cartData.registerWithAFIP 
                : (cartData.invoiceType === 'X' ? INVOICE_FEATURES.PRESUPUESTO_REGISTER_WITH_AFIP : true),
            // NEW: Split payment data for "Smart Otra" feature
            paymentMode: cartData.paymentMode || 'single',
            splitPayment: cartData.splitPayment || null
        };
        
        console.log('Creating invoice with payload:', payload);
        console.log('=== COMPLETE PAYLOAD SENT TO BACKEND ===');
        console.log('URL:', `${INVOICE_API_URL}/create`);
        console.log('Payload structure:');
        console.log('- cartItems:', payload.cartItems);
        console.log('- subtotal:', payload.subtotal);
        console.log('- total:', payload.total);
        console.log('- discountPercentage:', payload.discountPercentage);
        console.log('- discountAmount:', payload.discountAmount);
        console.log('- sessionId:', payload.sessionId);
        console.log('- externalReference:', payload.externalReference);
        console.log('- invoiceType:', payload.invoiceType);
        console.log('- paymentMethod:', payload.paymentMethod);
        console.log('- clientData:', payload.clientData);          // NEW
        console.log('- registerWithAFIP:', payload.registerWithAFIP); // NEW
        console.log('=== FULL JSON PAYLOAD ===');
        console.log(JSON.stringify(payload, null, 2));
        console.log('===============================');
        
        // Use axios with timeout for automatic token refresh support
        const controller = new AbortController();
        
        try {
            const response = await axios.post(
                `${INVOICE_API_URL}/create`,
                payload,
                {
                    signal: controller.signal,
                    timeout: 60000, // 60 seconds as recommended by backend guide
                    validateStatus: (status) => status === 200 || status === 202 // Accept both success statuses
                }
            );

            console.log('Response status:', response.status);
            
            // Handle status 202 (Queued - AFIP offline) differently from status 200 (Success - AFIP online)
            if (response.status === 202) {
                // Invoice was QUEUED due to AFIP being offline
                const queueData = response.data;
                console.log('=== INVOICE QUEUED (STATUS 202) ===');
                console.log('Queue response:', JSON.stringify(queueData, null, 2));
                console.log('- queueId:', queueData.queueId);
                console.log('- invoiceId:', queueData.invoiceId);
                console.log('- externalReference:', queueData.externalReference);
                console.log('===============================');
                
                // Return queue response with specific structure
                return {
                    success: true,
                    queued: true,
                    queueId: queueData.queueId,
                    invoiceId: queueData.invoiceId,
                    externalReference: queueData.externalReference,
                    message: queueData.message || 'Factura encolada - AFIP no disponible'
                };
            }
            
            // Status 200 - Invoice processed instantly with AFIP
            const result = handleApiResponse(response.data);
            console.log('=== COMPLETE BACKEND RESPONSE ===');
            console.log('Parsed result structure:', JSON.stringify(result, null, 2));
            console.log('=== BACKEND RESPONSE ANALYSIS ===');
            if (result.data) {
                console.log('- invoiceId:', result.data.invoiceId);
                console.log('- externalReference:', result.data.externalReference);
                console.log('- cae:', result.data.cae);
                console.log('- afipQr:', result.data.afipQr);
                console.log('- total:', result.data.total);
                console.log('- Available properties:', Object.keys(result.data));
                console.log('=== MISSING PROPERTIES CHECK ===');
                console.log('- numeroComprobante:', result.data.numeroComprobante || 'MISSING');
                console.log('- fechaEmision:', result.data.fechaEmision || 'MISSING');
                console.log('- fechaVencimiento:', result.data.fechaVencimiento || 'MISSING');
                console.log('- caeVencimiento:', result.data.caeVencimiento || 'MISSING');
                console.log('- puntoVenta:', result.data.puntoVenta || 'MISSING');
                
                // Auto-create Cuenta Corriente (AR) if payment method is Cuenta Corriente (204)
                if (payload.paymentMethod === '204' && result.data.invoiceId) {
                    console.log('[Invoice Service] Payment method is Cuenta Corriente - creating AR record');
                    try {
                        const { createCuentaCorriente } = await import('./cuentasCorrientesService');
                        
                        // Calculate due date (NET15 = 15 days from today)
                        const today = new Date();
                        const dueDate = new Date(today);
                        dueDate.setDate(dueDate.getDate() + 15);
                        
                        await createCuentaCorriente({
                            invoiceId: result.data.invoiceId,
                            clienteDocumento: transformedClientData?.documento_nro || transformedClientData?.cuit || '',
                            clienteNombre: transformedClientData?.razon_social || transformedClientData?.nombre || 'Cliente',
                            montoTotal: payload.total,
                            fechaVencimiento: formatDateLocal(dueDate),
                            condicionesPago: 'NET15'
                        });
                        console.log('[Invoice Service] Cuenta corriente created successfully for invoice', result.data.invoiceId);
                    } catch (arError) {
                        console.error('[Invoice Service] Error creating cuenta corriente (non-critical):', arError);
                        // Don't fail invoice creation if AR creation fails
                    }
                }
            }
            console.log('===============================');
            return result;
        } catch (axiosError) {
            // Handle axios timeout and cancellation errors
            if (axios.isCancel(axiosError) || axiosError.code === 'ECONNABORTED') {
                console.error('Invoice creation timeout after 60 seconds');
                throw new Error('La generación de factura está tomando más tiempo del esperado. El proceso puede continuar en segundo plano - por favor verifique en unos minutos.');
            }
            
            // Extract error message from axios error response
            const errorMessage = axiosError.response?.data?.message || axiosError.message;
            throw new Error(errorMessage);
        }
    } catch (error) {
        console.error('Error creating invoice:', error);
        throw new Error(`Invoice creation failed: ${error.message}`);
    }
};

/**
 * Get invoice history with pagination and filtering
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.dateFrom - Filter from date (optional)
 * @param {string} options.dateTo - Filter to date (optional)
 * @param {string} options.search - Search term for external reference (optional)
 * @returns {Promise<Object>} Invoice history response with pagination
 */
export const getInvoiceHistory = async (options = {}) => {
    try {
        const {
            page = 1,
            limit = 20,
            status,
            dateFrom,
            dateTo,
            search
        } = options;
        
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        
        if (status) params.append('status', status);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        if (search) params.append('search', search);
        
        const response = await axios.get(`${INVOICE_API_URL}?${params.toString()}`);
        return handleApiResponse(response.data);
    } catch (error) {
        console.error('Error fetching invoice history:', error);
        throw new Error(`Failed to fetch invoice history: ${error.message}`);
    }
};

/**
 * Get specific invoice by ID
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<Object>} Invoice details with items
 */
export const getInvoice = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${INVOICE_API_URL}/${invoiceId}`);
        return handleApiResponse(response.data);
    } catch (error) {
        console.error('Error fetching invoice:', error);
        throw new Error(`Failed to fetch invoice: ${error.message}`);
    }
};

/**
 * Retry failed invoice generation
 * @param {number} invoiceId - Invoice ID to retry
 * @returns {Promise<Object>} Retry response
 */
export const retryInvoice = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.post(`${INVOICE_API_URL}/retry/${invoiceId}`);
        return handleApiResponse(response.data);
    } catch (error) {
        console.error('Error retrying invoice:', error);
        throw new Error(`Failed to retry invoice: ${error.message}`);
    }
};

/**
 * Get invoice audit trail
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<Object>} Audit trail data
 */
export const getInvoiceAudit = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${INVOICE_API_URL}/${invoiceId}/audit`);
        return handleApiResponse(response.data);
    } catch (error) {
        console.error('Error fetching invoice audit:', error);
        throw new Error(`Failed to fetch invoice audit: ${error.message}`);
    }
};

/**
 * Download invoice PDF
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<string>} PDF URL for download/viewing
 */
export const getInvoicePDF = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${INVOICE_API_URL}/${invoiceId}/pdf`);
        const result = handleApiResponse(response.data);
        return result.data.pdfUrl;
    } catch (error) {
        console.error('Error getting invoice PDF:', error);
        throw new Error(`Failed to get invoice PDF: ${error.message}`);
    }
};

/**
 * Get invoice QR code for AFIP verification
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<string>} QR code URL
 */
export const getInvoiceQR = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${INVOICE_API_URL}/${invoiceId}/qr`);
        const result = handleApiResponse(response.data);
        return result.data.qrUrl;
    } catch (error) {
        console.error('Error getting invoice QR:', error);
        throw new Error(`Failed to get invoice QR: ${error.message}`);
    }
};

/**
 * Utility function to format cart data for invoice creation
 * @param {Object} cartContext - Cart context from CartProvider
 * @param {string} sessionId - Optional session ID
 * @returns {Object} Formatted cart data for invoice API
 */
export const formatCartForInvoice = (cartContext, sessionId = null) => {
    const {
        cartDetails = [],
        subTotalPrice = 0,
        totalAfterDiscount = 0,
        discountPercentage = 0,
        invoiceType,        // NEW: Preserve invoice type
        paymentMethod,      // NEW: Preserve payment method
        clientData,         // NEW: Preserve client data
        paymentMode,        // NEW: 'single' or 'split' for Smart Otra
        splitPayment,       // NEW: Split payment details { payment1, payment2 }
        isBudgetQuote,      // NEW: For stock control
        registerWithAFIP,   // NEW: For AFIP registration
        getDisplayRetailPrice, // NEW: For price conversion
        isVolumePricingActive, // NEW: Check if volume pricing is active
        getVolumePrice     // NEW: Get volume-adjusted price
    } = cartContext;
    
    // Backend amplifies the discount, so we send SIMPLE discount (not amplified)
    // Frontend displays the amplified calculation, but backend needs simple input
    const simpleDiscountAmount = subTotalPrice * ((discountPercentage || 0) / 100);
    
    // Build base payload - send simple discount, backend will amplify and match our display
    const basePayload = {
        cartItems: cartDetails.map(item => {
            // Calculate the effective price considering volume pricing
            let effectiveRetailPrice = item.RetailPrice || 0;
            
            // Apply volume pricing if active (only for non-custom products)
            if (!item.isCustom && typeof isVolumePricingActive === 'function' && typeof getVolumePrice === 'function' && typeof getDisplayRetailPrice === 'function') {
                const basePrice = getDisplayRetailPrice(item.RetailPrice);
                const isVolumeActive = isVolumePricingActive(item.productId);
                
                if (isVolumeActive) {
                    effectiveRetailPrice = getVolumePrice(basePrice, item.productId);
                    console.log(`📊 Volume pricing applied for ${item.ProductCode}: ${item.RetailPrice} → ${effectiveRetailPrice}`);
                }
            }
            
            return {
                ProductId: item.ProductId,
                ProductDescription: item.ProductDescription || 'Producto sin descripción',
                ProductCode: item.ProductCode || '',
                EAN: item.EAN || '',
                RetailPrice: effectiveRetailPrice,
                BrandName: item.BrandName || '',
                CategoryName: item.CategoryName || '',
                quantity: item.quantity || 1,
                splitter: item.splitter || 1,
                StockQuantity: item.StockQuantity || 0
            };
        }),
        subtotal: subTotalPrice,
        total: totalAfterDiscount,  // Frontend display value: $864.51 (backend will recalculate anyway)
        discountPercentage: discountPercentage || 0,
        discountAmount: simpleDiscountAmount,  // Simple: $101.28 (backend will amplify to $122.55)
        sessionId: sessionId || `cart_${Date.now()}`,
        // Generate external reference using organization + timestamp
        externalReference: generateExternalReference(),
        customReference: generateExternalReference(), // Try both property names
        // NEW: Pass through invoice configuration
        invoiceType: invoiceType || 'C',           // Default to Factura C if not specified
        paymentMethod: paymentMethod || '201',     // Default to Contado if not specified
        clientData: clientData || null,            // Client data (null for C/X)
        isBudgetQuote: isBudgetQuote === true,     // For stock control (only BUDGET_QUOTE docs)
        registerWithAFIP: registerWithAFIP !== false // For AFIP registration
    };
    
    // Add split payment data if applicable ("Smart Otra" feature)
    if (paymentMode === 'split' && splitPayment) {
        basePayload.paymentMode = 'split';
        basePayload.splitPayment = {
            payment1: {
                method: splitPayment.payment1.method,
                name: splitPayment.payment1.name,
                amount: splitPayment.payment1.amount,
                percentage: totalAfterDiscount > 0 
                    ? ((splitPayment.payment1.amount / totalAfterDiscount) * 100).toFixed(2)
                    : '0.00'
            },
            payment2: {
                method: splitPayment.payment2.method,
                name: splitPayment.payment2.name,
                amount: splitPayment.payment2.amount,
                percentage: totalAfterDiscount > 0 
                    ? ((splitPayment.payment2.amount / totalAfterDiscount) * 100).toFixed(2)
                    : '0.00'
            }
        };
        // For AFIP, use code 208 (Otra) when splitting payment
        basePayload.paymentMethod = '208';
        
        console.log('[Invoice Service] Split payment data prepared:', basePayload.splitPayment);
    } else {
        basePayload.paymentMode = 'single';
    }
    
    return basePayload;
};

/**
 * Invoice status constants
 */
export const INVOICE_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    ERROR: 'error',
    CANCELLED: 'cancelled'
};

//----------------------------------------------------------------------------------------------------------------------------------------------------------//
//----------------------------------------------------------------------------------------------------------------------------------------------------------//
// V2 API - BACKEND AS SINGLE SOURCE OF TRUTH FOR CALCULATIONS
//----------------------------------------------------------------------------------------------------------------------------------------------------------//
//----------------------------------------------------------------------------------------------------------------------------------------------------------//

/**
 * V2: Calculate cart totals using backend (Real-time preview)
 * Called on every cart change (debounced) to get accurate totals from backend
 */
export const calculateCartV2 = async (cartData) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
        const { 
            cartDetails, 
            discountPercentage = 0, 
            invoiceType = 'C',
            getDisplayRetailPrice,
            isVolumePricingActive,
            getVolumePrice
        } = cartData;
        
        if (!cartDetails || cartDetails.length === 0) {
            throw new Error('Cart is empty');
        }
        
        // Get clientDatabaseName from cartData or fetch from localStorage
        const clientDatabaseName = cartData.clientDatabaseName || getClientDatabaseName();
        
        if (!clientDatabaseName) {
            throw new Error('clientDatabaseName is required for V2 calculations');
        }
        
        const payload = {
            cartItems: cartDetails.map(item => {
                // V2 API: Send only raw cart data - backend queries prices from database
                const baseItem = {
                    ProductId: item.ProductId || item.productId,  // CRITICAL: Send ProductId to avoid duplicate ProductCode issues
                    ProductCode: item.ProductCode,
                    ProductName: item.ProductDescription || item.ProductName,
                    quantity: item.quantity,
                    splitter: item.splitter || 1
                };
                
                // Special case: Custom products CAN include RetailPrice (backend accepts it)
                const isCustomProduct = item.ProductCode === 'CUSTOM' || 
                                       item.ProductCode === 'CustomProduct' || 
                                       item.ProductCode === null || 
                                       item.ProductId === null;
                
                if (isCustomProduct && item.RetailPrice) {
                    baseItem.RetailPrice = item.RetailPrice;
                } else if (!isCustomProduct && typeof isVolumePricingActive === 'function') {
                    // For non-custom products, check if volume pricing is active
                    const isVolumeActive = isVolumePricingActive(item.productId);
                    
                    console.log(`🔍 [formatCartForInvoice] Checking volume pricing for ${item.ProductCode}:`, {
                        productId: item.productId,
                        ProductId: item.ProductId,
                        isVolumeActive,
                        hasFunction: typeof isVolumePricingActive === 'function'
                    });
                    
                    // Send the volume pricing state to backend so it can calculate
                    baseItem.isVolumePricingActive = isVolumeActive;
                    
                    if (isVolumeActive && typeof getVolumePrice === 'function' && typeof getDisplayRetailPrice === 'function') {
                        // Calculate and send the volume-adjusted price to backend
                        const basePrice = getDisplayRetailPrice(item.RetailPrice);
                        const volumePrice = getVolumePrice(basePrice, item.productId, item.quantity);
                        
                        // CRITICAL: Send the volume price to backend
                        baseItem.RetailPrice = volumePrice;
                        
                        console.log(`📊 V2 Calculate: Volume pricing for ${item.ProductCode}:`, {
                            originalPrice: item.RetailPrice,
                            basePrice,
                            volumePrice,
                            quantity: item.quantity,
                            sentToBackend: baseItem.RetailPrice
                        });
                    }
                }
                
                console.log(`📤 [Sending to backend] ${item.ProductCode}:`, baseItem);
                
                return baseItem;
            }),
            discount: {
                percentage: discountPercentage || 0
            },
            invoiceType,
            clientDatabaseName
        };
        
        console.log('🔵 [V2 Calculate] Requesting cart calculation:', {
            items: payload.cartItems.length,
            discount: `${discountPercentage}%`,
            invoiceType,
            timestamp: new Date().toISOString()
        });
        console.log('📦 [V2 Calculate] Full payload:', payload);
        
        const response = await axios.post(
            `${INVOICE_API_URL}/v2/calculate-cart`,
            payload,
            {
                signal: controller.signal,
                timeout: 10000
            }
        );
        
        const result = response.data;
        
        console.log('✅ [V2 Calculate] Received calculation:', {
            total: result.calculation?.total,
            discount: result.calculation?.discount,
            subtotalSinIva: result.calculation?.subtotalSinIva,
            source: 'backend-v2'
        });
        
        return result.calculation;
        
    } catch (error) {
        if (axios.isCancel(error) || error.code === 'ECONNABORTED') {
            console.error('⏱️ [V2 Calculate] Timeout - calculation took > 10s');
            throw new Error('Calculation timeout - server too slow');
        }
        
        // Handle axios error response
        if (error.response) {
            console.error('❌ [V2 Calculate] Backend error:', error.response.status, error.response.data);
            throw new Error(`Calculation failed: HTTP ${error.response.status}`);
        }
        
        console.error('❌ [V2 Calculate] Failed:', error.message);
        throw error;
    }
};

/**
 * V2: Create invoice using backend calculations only
 * @param {Object} cartData - Cart data with items, client, payment info
 * @param {Object} volumePricingFunctions - Volume pricing helper functions from CartContext
 * @param {Function} volumePricingFunctions.getVolumePrice - Get volume-adjusted price
 * @param {Function} volumePricingFunctions.getDisplayRetailPrice - Get display retail price
 * @param {Function} volumePricingFunctions.isVolumePricingActive - Check if volume pricing active
 */
export const createInvoiceV2 = async (cartData, volumePricingFunctions = {}) => {
    try {
        const { getVolumePrice, getDisplayRetailPrice, isVolumePricingActive: isVolumePricingActiveFn } = volumePricingFunctions;
        
        let transformedClientData = cartData.clientData;
        if (transformedClientData && transformedClientData.condicion_impositiva) {
            transformedClientData = {
                ...transformedClientData,
                condicion_iva: mapTaxConditionToAFIPCode(transformedClientData.condicion_impositiva)
            };
        }
        
        const isBudgetQuote = cartData.isBudgetQuote === true;
        const transformedCartItems = transformCartItemsWithIVA(
            cartData.cartItems, 
            cartData.invoiceType || 'C',
            isBudgetQuote
        );
        
        // Get clientDatabaseName from cartData or fetch from localStorage
        const clientDatabaseName = cartData.clientDatabaseName || getClientDatabaseName();
        
        if (!clientDatabaseName) {
            throw new Error('clientDatabaseName is required for invoice creation');
        }
        
        const payload = {
            cartItems: transformedCartItems.map((item, index) => {
                // V2 API: Send only required fields - backend calculates prices
                const baseItem = {
                    ProductCode: item.ProductCode,
                    ProductName: item.ProductDescription || item.ProductName,
                    ProductId: item.ProductId,
                    quantity: item.quantity,
                    splitter: item.splitter || 1
                };
                
                // Special case: Custom products CAN include RetailPrice
                const isCustomProduct = item.ProductCode === 'CUSTOM' || 
                                       item.ProductCode === 'CustomProduct' || 
                                       item.ProductCode === null || 
                                       item.ProductId === null;
                
                if (isCustomProduct && item.RetailPrice) {
                    baseItem.RetailPrice = item.RetailPrice;
                }
                
                // ✅ Running Account invoicing: Force use of historical price (already includes any discount)
                // forceRetailPrice is set when invoicing from Cuenta Abierta where we need the CARGO-time price
                const originalItem = cartData.cartItems[index];
                if (originalItem?.forceRetailPrice && originalItem.RetailPrice) {
                    baseItem.RetailPrice = originalItem.RetailPrice;
                    // ✅ Tell backend to use the provided RetailPrice instead of looking up from DB
                    baseItem.isVolumePricingActive = true;
                    console.log(`📌 [V2 Create] Using forced RetailPrice for ${item.ProductCode}:`, {
                        ProductId: item.ProductId,
                        RetailPrice: originalItem.RetailPrice,
                        isVolumePricingActive: true,
                        reason: 'Running Account historical price'
                    });
                }
                
                // CRITICAL: Preserve isVolumePricingActive from original cartData
                // transformCartItemsWithIVA creates new objects, so we need to get it from original
                if (originalItem && originalItem.isVolumePricingActive !== undefined) {
                    baseItem.isVolumePricingActive = originalItem.isVolumePricingActive;
                    
                    // ✅ FIX: Calculate and send the volume-adjusted RetailPrice for volume-priced items
                    // We need to calculate it the same way calculateCartV2 does
                    const isVolumeActive = originalItem.isVolumePricingActive === true;
                    
                    if (isVolumeActive && typeof getVolumePrice === 'function' && typeof getDisplayRetailPrice === 'function') {
                        // Calculate volume-adjusted price (same logic as calculateCartV2)
                        const basePrice = getDisplayRetailPrice(originalItem.RetailPrice);
                        const volumePrice = getVolumePrice(basePrice, originalItem.productId, originalItem.quantity);
                        
                        // Send the calculated volume price to backend
                        baseItem.RetailPrice = volumePrice;
                        
                        console.log(`✅ [V2 Create] Calculated and sending volume price for ${item.ProductCode}:`, {
                            ProductId: item.ProductId,
                            ProductCode: item.ProductCode,
                            quantity: item.quantity,
                            splitter: item.splitter,
                            databasePrice: originalItem.RetailPrice,
                            basePriceAfterDisplay: basePrice,
                            volumePriceCalculated: volumePrice,
                            isVolumePricingActive: true,
                            note: 'Backend will divide by 1.21 and apply to invoice'
                        });
                    } else if (isVolumeActive && (!getVolumePrice || !getDisplayRetailPrice)) {
                        console.warn(`⚠️ [V2 Create] Volume pricing active for ${item.ProductCode} but helper functions not provided!`);
                    }
                }
                
                return baseItem;
            }),
            discount: {
                percentage: cartData.discountPercentage || 0
            },
            invoiceType: cartData.invoiceType,
            paymentMethod: cartData.paymentMethod,
            clientData: transformedClientData,
            clientTaxCondition: cartData.clientTaxCondition || 'CF', // Add client tax condition (default CF)
            clientDatabaseName: clientDatabaseName, // Add clientDatabaseName to payload
            registerWithAFIP: cartData.registerWithAFIP !== undefined 
                ? cartData.registerWithAFIP 
                : (cartData.invoiceType === 'X' ? INVOICE_FEATURES.PRESUPUESTO_REGISTER_WITH_AFIP : true),
            paymentMode: cartData.paymentMode || 'single',
            splitPayment: cartData.splitPayment || null,
            sessionId: cartData.sessionId || `cart_${Date.now()}`,
            externalReference: generateExternalReference(),
            isPaid: cartData.isPaid === true,      // RA pre-paid gate (analytics exclusion + Caja bypass)
            invoiceSource: cartData.invoiceSource || null
        };
        
        console.log('🔵 [V2 Create] Creating invoice - NO TOTALS SENT');
        console.log('📍 [V2 Create] Using clientDatabaseName:', clientDatabaseName);
        console.log('📍 [V2 Create] clientTaxCondition in payload:', payload.clientTaxCondition);
        console.log('⚡ [V2 Create] Cart items with volume pricing flags:', payload.cartItems.map(item => ({
            ProductCode: item.ProductCode,
            ProductId: item.ProductId,
            isVolumePricingActive: item.isVolumePricingActive
        })));
        console.log('📦 [V2 Create] Full payload being sent:', JSON.stringify(payload, null, 2));
        
        const controller = new AbortController();
        
        try {
            const response = await axios.post(
                `${INVOICE_API_URL}/v2/create`,
                payload,
                {
                    signal: controller.signal,
                    timeout: 60000, // 60 seconds for invoice creation
                    validateStatus: (status) => status === 200 || status === 202
                }
            );
            
            if (response.status === 202) {
                const queueData = response.data;
                // Backend returns: { success, queued, message, invoice: {...}, version, calculationSource }
                // Return the data as-is since it's already in the correct nested format
                return queueData;
            }
            
            const result = handleApiResponse(response.data);
            
            console.log('✅ [V2 Create] Invoice created:', {
                invoiceId: result.invoice?.invoice_id || result.invoiceId,
                total: result.calculation?.total || result.invoice?.total
            });
            
            if (payload.paymentMethod === '204' && (result.invoice?.invoice_id || result.invoiceId)) {
                try {
                    const { createCuentaCorriente } = await import('./cuentasCorrientesService');
                    const today = new Date();
                    const dueDate = new Date(today);
                    dueDate.setDate(dueDate.getDate() + 15);
                    
                    await createCuentaCorriente({
                        invoiceId: result.invoice?.invoice_id || result.invoiceId,
                        clienteDocumento: transformedClientData?.documento_nro || transformedClientData?.cuit || '',
                        clienteNombre: transformedClientData?.razon_social || transformedClientData?.nombre || 'Cliente',
                        montoTotal: result.calculation?.total || result.invoice?.total,
                        fechaVencimiento: formatDateLocal(dueDate),
                        condicionesPago: 'NET15'
                    });
                } catch (arError) {
                    console.error('[V2 Create] Error creating AR (non-critical):', arError);
                }
            }
            
            return result;
            
        } catch (axiosError) {
            if (axios.isCancel(axiosError) || axiosError.code === 'ECONNABORTED') {
                throw new Error('Invoice creation timeout - server took > 60 seconds');
            }
            
            // Extract error message from axios error
            const errorMessage = axiosError.response?.data?.message || axiosError.message;
            throw new Error(errorMessage);
        }
        
    } catch (error) {
        console.error('❌ [V2 Create] Invoice creation failed:', error.message);
        throw error;
    }
};

/**
 * Export all functions for use in components
 */
export default {
    checkAmountLimit,
    createInvoice,
    getInvoiceHistory,
    getInvoice,
    retryInvoice,
    getInvoiceAudit,
    getInvoicePDF,
    getInvoiceQR,
    formatCartForInvoice,
    calculateCartV2,
    createInvoiceV2,
    INVOICE_STATUS
};